import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../api';
import hadiyaLogo from '../assets/hadiya-logo-web.png';

const OngoingTimer = ({ initialTime }) => {
  const [timeLeft, setTimeLeft] = useState(initialTime);
  
  useEffect(() => {
    if (timeLeft <= 0) return;
    const int = setInterval(() => setTimeLeft(t => t - 1), 1000);
    return () => clearInterval(int);
  }, [timeLeft]);

  if (timeLeft <= 0) return <span style={{color: '#e53e3e'}}>Expired</span>;
  
  const m = Math.floor(timeLeft / 60);
  const s = timeLeft % 60;
  return <span style={{color: '#dd6b20', fontWeight: 'bold'}}>{m}:{s.toString().padStart(2, '0')}</span>;
};

export default function Dashboard({ isTestChannel = false }) {
  const [user, setUser] = useState(null);
  const [exams, setExams] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEligibleForEvent, setIsEligibleForEvent] = useState(null);
  const [publishedResult, setPublishedResult] = useState(null);
  const [publishedResultName, setPublishedResultName] = useState('');
  const [dailyQuizResultAvailable, setDailyQuizResultAvailable] = useState(false);
  const navigate = useNavigate();
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [selectedExamId, setSelectedExamId] = useState(null);

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (!u) return navigate('/exam');
    setUser(JSON.parse(u));
    loadExams();
  }, [navigate]);

  const loadExams = async () => {
    try {
      // Load exams
      const { data } = await api.get(`/exam/questionnaires?testChannel=${isTestChannel}`);
      setExams(data);
      
      // Load articles
      try {
        const artData = await api.get(`/articles?testChannel=${isTestChannel}`);
        setArticles(artData.data);
      } catch (e) {
        console.error("Articles fetch failed");
      }
      
      // Load published results
      try {
        const resultData = await api.get('/exam/published-results');
        if (resultData.data.available) {
          setPublishedResult(resultData.data.questionnaire_id);
          setPublishedResultName(resultData.data.questionnaire_name);
        }
      } catch (e) {
        console.error("Result check failed");
      }

      // Load daily quiz results status
      try {
        const dqResult = await api.get(`/exam/daily-quiz-results-status?testChannel=${isTestChannel}`);
        setDailyQuizResultAvailable(dqResult.data.available);
      } catch (e) {
        console.error("Daily quiz result check failed");
      }

      if (data.length === 0) {
        try {
          const eligData = await api.get('/exam/eligibility/36');
          setIsEligibleForEvent(eligData.data.eligible);
        } catch (e) {
          console.error("Eligibility check failed");
        }
      }
    } catch (err) {
      if (err.response?.status === 401) navigate('/exam');
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.clear();
    navigate('/exam');
  };

  return (
    <div style={styles.container}>
      <div style={styles.backgroundPattern}></div>
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <img src={hadiyaLogo} alt="Hadiya Logo" style={styles.headerLogo} />
          <div style={styles.userInfo}>
            <h1 style={styles.welcomeText}>Welcome, {user?.name}</h1>
            <p style={styles.userDetails}>
              <span style={styles.detailItem}>ID: {user?.roll_no}</span>
            </p>
          </div>
        </div>
        <button onClick={logout} style={styles.logoutBtn}>
          Logout
        </button>
      </header>
      
      <div style={styles.content}>

        
        {loading ? (
          <div style={styles.loadingState}>
            <div style={styles.loadingSpinner}>...</div>
            <p style={styles.loadingText}>Loading dashboard...</p>
          </div>
        ) : (exams.length === 0 && !publishedResult && !dailyQuizResultAvailable && articles.length === 0) ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#718096', backgroundColor: 'rgba(255,255,255,0.8)', borderRadius: '16px', margin: '20px' }}>
            <h2>No active content available</h2>
            <p>Please check back later for new quizzes or articles.</p>
          </div>
        ) : (
          <div style={styles.grid}>
            {publishedResult && (
              <div style={{...styles.card, border: '2px solid #48bb78'}}>
                <div style={styles.cardHeader}>
                  <div style={{...styles.cardIcon, color: '#48bb78'}}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
                      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
                      <path d="M4 22h16"></path>
                      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path>
                      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path>
                      <path d="M18 2H6v7a6 6 0 0 0 12 0V2z"></path>
                    </svg>
                  </div>
                  <div style={{...styles.cardBadge, background: '#48bb78', color: 'white'}}>PUBLISHED</div>
                </div>
                <h3 style={styles.cardTitle}>{publishedResultName}</h3>
                <div style={styles.cardMeta}>
                </div>
                <Link to={`/student-react/result/${publishedResult}${isTestChannel ? '?testChannel=true' : ''}`} style={{...styles.startBtn, background: '#48bb78', marginTop: '16px'}}>
                  View Result
                </Link>
              </div>
            )}

            {dailyQuizResultAvailable && (
              <div style={{...styles.card, border: '2px solid #38b2ac'}}>
                <div style={styles.cardHeader}>
                  <div style={{...styles.cardIcon, color: '#38b2ac'}}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="20" x2="18" y2="10"></line>
                      <line x1="12" y1="20" x2="12" y2="4"></line>
                      <line x1="6" y1="20" x2="6" y2="14"></line>
                    </svg>
                  </div>
                  <div style={{...styles.cardBadge, background: '#38b2ac', color: 'white'}}>RESULTS</div>
                </div>
                <h3 style={styles.cardTitle}>Hadiya Daily Quiz Results</h3>
                <div style={styles.cardMeta}>
                  <div style={styles.metaItem}>
                    <span style={styles.metaIcon}>View your overall performance</span>
                  </div>
                </div>
                <Link to={`/student-react/daily-results${isTestChannel ? '?testChannel=true' : ''}`} style={{...styles.startBtn, background: '#38b2ac', marginTop: '16px'}}>
                  View Daily Results
                </Link>
              </div>
            )}

            {/* Article Cards fetched from DB */}
            {articles.map((article) => (
              <div key={`art-${article.id}`} style={{...styles.card, border: '2px solid #4c51bf'}}>
                <div style={styles.cardHeader}>
                  <div style={{...styles.cardIcon, color: '#4c51bf'}}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                    </svg>
                  </div>
                  <div style={{...styles.cardBadge, background: '#4c51bf', color: 'white'}}>ARTICLE</div>
                </div>
                <h3 style={styles.cardTitle}>{article.title}</h3>
                {article.sub_title && <p style={styles.cardDescription}>{article.sub_title}</p>}
                
                <div style={{ display: 'flex', gap: '10px', marginTop: 'auto', flexDirection: 'column' }}>
                  {article.url_malayalam && (
                    <button 
                      onClick={() => navigate(`/student-react/article?url=${encodeURIComponent(article.url_malayalam)}${isTestChannel ? '&testChannel=true' : ''}`)}
                      style={{...styles.startBtn, background: 'linear-gradient(135deg, #4c51bf 0%, #6b46c1 100%)', width: '100%'}}
                    >
                      Read in Malayalam
                    </button>
                  )}
                  {article.url_english && (
                    <button 
                      onClick={() => navigate(`/student-react/article?url=${encodeURIComponent(article.url_english)}${isTestChannel ? '&testChannel=true' : ''}`)}
                      style={{...styles.startBtn, background: 'linear-gradient(135deg, #4a5568 0%, #2d3748 100%)', width: '100%'}}
                    >
                      Read in English
                    </button>
                  )}
                </div>
              </div>
            ))}

            {exams.map(ex => (
              <div key={ex.id} style={styles.card}>
                <div style={styles.cardHeader}>
                  <div style={{...styles.cardIcon, color: '#ed8936'}}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                  </div>
                  <div style={{...styles.cardBadge, background: '#ed8936', color: 'white'}}>QUIZ</div>
                </div>
                <h3 style={styles.cardTitle}>{ex.title || ex.name}</h3>
                <div style={styles.cardMeta}>
                  <div style={styles.metaItem}>
                    <span style={styles.metaIcon}>Time</span>
                    <span>{ex.ongoing ? <OngoingTimer initialTime={ex.remaining_time} /> : `10 min`}</span>
                  </div>

                </div>
                {ex.is_eligible !== false && (
                  <button 
                    onClick={() => { 
                      if (ex.ongoing) {
                        navigate(`/student-react/test/${ex.id}${isTestChannel ? '?testChannel=true' : ''}`);
                      } else {
                        setSelectedExamId(ex.id); 
                        setShowLanguageModal(true); 
                      }
                    }} 
                    style={ex.ongoing ? {...styles.startBtn, background: 'linear-gradient(135deg, #ed8936 0%, #dd6b20 100%)'} : styles.startBtn}
                  >
                    <span style={styles.btnIcon}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="5 3 19 12 5 21 5 3"></polygon>
                      </svg>
                    </span>
                    {ex.ongoing ? 'Immediate Join' : 'Attend Quiz'}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {showLanguageModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          backdropFilter: 'blur(3px)'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '16px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
          }}>
            <h2 style={{ color: '#2d3748', fontSize: '20px', marginBottom: '25px', marginTop: 0 }}>Select Quiz Language<br/><span style={{fontSize: '16px', fontWeight: 'normal'}}>ക്വിസ്സിന്റെ ഭാഷ തിരഞ്ഞെടുക്കുക</span></h2>
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexDirection: 'column' }}>
              <button 
                onClick={() => {
                  setShowLanguageModal(false);
                  navigate(`/student-react/test/${selectedExamId}${isTestChannel ? '?testChannel=true' : ''}`, { state: { language: 'malayalam' } });
                }}
                style={{
                  padding: '14px 24px',
                  backgroundColor: '#4c51bf',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                Malayalam (മലയാളം)
              </button>
              <button 
                onClick={() => {
                  setShowLanguageModal(false);
                  navigate(`/student-react/test/${selectedExamId}${isTestChannel ? '?testChannel=true' : ''}`, { state: { language: 'english' } });
                }}
                style={{
                  padding: '14px 24px',
                  backgroundColor: '#4c51bf',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                English
              </button>
              <button 
                onClick={() => setShowLanguageModal(false)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#edf2f7',
                  color: '#4a5568',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  marginTop: '10px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    padding: '0',
    position: 'relative',
  },
  backgroundPattern: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: 'radial-gradient(circle at 10% 20%, rgba(102, 126, 234, 0.05) 0%, transparent 50%), radial-gradient(circle at 90% 80%, rgba(118, 75, 162, 0.05) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  header: {
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px)',
    padding: '16px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
  },
  headerLogo: {
    height: '40px',
    objectFit: 'contain',
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  welcomeText: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '700',
    color: '#1a202c',
    letterSpacing: '-0.5px',
  },
  userDetails: {
    margin: '2px 0 0',
    fontSize: '12px',
    color: '#718096',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  detailItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  detailSeparator: {
    color: '#cbd5e0',
  },
  logoutBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 16px',
    background: 'linear-gradient(135deg, #f56565 0%, #c53030 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 15px rgba(245, 101, 101, 0.3)',
    whiteSpace: 'nowrap',
  },
  logoutIcon: {
    fontSize: '18px',
  },
  content: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
  },
  sectionTitle: {
    margin: 0,
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a202c',
    letterSpacing: '-0.5px',
  },
  examCount: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '600',
  },
  loadingState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
  },
  loadingSpinner: {
    fontSize: '48px',
    marginBottom: '16px',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    margin: 0,
    fontSize: '16px',
    color: '#718096',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    background: 'rgba(255, 255, 255, 0.8)',
    borderRadius: '24px',
    backdropFilter: 'blur(10px)',
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '24px',
  },
  emptyTitle: {
    margin: '0 0 12px',
    fontSize: '24px',
    fontWeight: '700',
    color: '#1a202c',
  },
  emptyText: {
    margin: 0,
    fontSize: '16px',
    color: '#718096',
    textAlign: 'center',
    maxWidth: '400px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '24px',
  },
  card: {
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: '20px',
    padding: '28px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s ease',
    border: '1px solid rgba(255, 255, 255, 0.5)',
    display: 'flex',
    flexDirection: 'column',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
  },
  cardIcon: {
    fontSize: '32px',
  },
  cardBadge: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  cardTitle: {
    margin: '0 0 12px',
    fontSize: '20px',
    fontWeight: '700',
    color: '#1a202c',
    letterSpacing: '-0.3px',
  },
  cardDescription: {
    margin: '0 0 20px',
    fontSize: '14px',
    color: '#718096',
    lineHeight: '1.6',
    flex: 1,
  },
  cardMeta: {
    display: 'flex',
    gap: '16px',
    marginBottom: '20px',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#4a5568',
    fontWeight: '500',
  },
  metaIcon: {
    fontSize: '16px',
  },
  startBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '14px 24px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
    border: 'none',
    cursor: 'pointer',
  },
  btnIcon: {
    fontSize: '16px',
  },
};
